import { type FC, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { PlaybookEditorContainer } from "./playbookEditorContainer.js";
import { usePlaybookEditorOutletContext } from "./playbookEditorOutletContext.js";
import {
  getSelectionIdForAssessmentKey,
  LEGACY_CRITERION_QUERY_PARAM,
  PLAYBOOK_SELECTION_QUERY_PARAM,
  resolveEditPlaybookUrlState,
} from "./playbookEditorUrlState.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export const PlaybookEditPage: FC = () => {
  const context = usePlaybookEditorOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, editorProps, onTabSelectionChange, onViewedTreeItemChange } =
    context;

  const urlState = useMemo(
    () => (data ? resolveEditPlaybookUrlState(searchParams, data.tree) : null),
    [data, searchParams],
  );
  const currentQuery = searchParams.toString();
  const canonicalQuery = urlState?.searchParams.toString();
  useEffect(() => {
    if (
      data &&
      canonicalQuery !== undefined &&
      canonicalQuery !== currentQuery
    ) {
      setSearchParams(
        (current) =>
          resolveEditPlaybookUrlState(current, data.tree).searchParams,
        { replace: true },
      );
    }
  }, [canonicalQuery, currentQuery, data, setSearchParams]);

  useEffect(
    () => () => {
      onViewedTreeItemChange(undefined);
    },
    [onViewedTreeItemChange],
  );

  const handleTreeItemChange = useCallback(
    (key: AssessmentTreeItemKey | undefined) => {
      onViewedTreeItemChange(key);
      const selectionId = getSelectionIdForAssessmentKey(key);
      onTabSelectionChange(selectionId);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(LEGACY_CRITERION_QUERY_PARAM);
      if (selectionId) {
        nextSearchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, selectionId);
      } else {
        nextSearchParams.delete(PLAYBOOK_SELECTION_QUERY_PARAM);
      }
      if (nextSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(nextSearchParams, { replace: true });
      }
    },
    [
      onViewedTreeItemChange,
      onTabSelectionChange,
      searchParams,
      setSearchParams,
    ],
  );

  const editor = (
    <PlaybookEditorContainer
      {...editorProps}
      initialSelectedKey={urlState?.selectionKey}
      onCurrentSelectionChange={handleTreeItemChange}
    />
  );

  // The embedded configuration-agent panel is retired — playbook configuration
  // now happens through Ask Alpharun, which deep-links back into this editor.
  return <div className="flex min-h-0 flex-1 flex-col p-2">{editor}</div>;
};
